<template>
  <fieldset class="currency-toggle" :disabled="disabled">
    <legend class="visually-hidden">Show credit figures in</legend>
    <label
      v-for="code in currencies"
      :key="code"
      class="currency-toggle__option"
      :class="{ 'is-selected': code === creditCurrency }"
    >
      <input
        class="visually-hidden"
        type="radio"
        name="credit-currency"
        :value="code"
        :checked="code === creditCurrency"
        @change="select(code)"
      />
      <span>{{ code }}</span>
    </label>
  </fieldset>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

// Radio inputs rather than buttons: single-select is what a radio group means, and
// it brings arrow-key navigation and the checked state with it instead of us
// reimplementing both on a row of buttons.
export default {
  name: "CurrencyToggle",
  props: {
    // Set while a refetch is in flight, so a second switch cannot race the first
    // and land the wrong currency's figures.
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["change"],
  data() {
    return {
      currencies: ["NGN", "USD", "KES"],
    };
  },
  computed: {
    ...mapGetters("organisationModule", ["creditCurrency"]),
  },
  methods: {
    ...mapActions("organisationModule", ["setCreditCurrency"]),
    select(code) {
      if (code === this.creditCurrency) return;
      this.setCreditCurrency(code);
      this.$emit("change", code);
    },
  },
};
</script>

<style scoped>
.currency-toggle {
  display: inline-flex;
  padding: 4px;
  margin: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f0f4fd;
}

.currency-toggle:disabled {
  opacity: 0.55;
}

.currency-toggle__option {
  position: relative;
  cursor: pointer;
}

.currency-toggle:disabled .currency-toggle__option {
  cursor: default;
}

.currency-toggle__option > span {
  display: block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.02em;
  color: #4c5b90;
  transition: color 150ms ease-out, background-color 150ms ease-out,
    box-shadow 150ms ease-out, transform 150ms ease-out;
}

.currency-toggle__option:hover > span {
  color: #2d303d;
}

/* The selected chip is the only element here that lifts, so it reads as chosen
   without a second border competing with the track's. */
.currency-toggle__option.is-selected > span {
  font-weight: 600;
  color: #1a2139;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(26, 33, 57, 0.08);
}

.currency-toggle:not(:disabled) .currency-toggle__option:active > span {
  transform: scale(0.97);
}

.currency-toggle__option input:focus-visible + span {
  outline: 2px solid #2a62f5;
  outline-offset: 2px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .currency-toggle__option > span {
    transition: none;
  }

  .currency-toggle:not(:disabled) .currency-toggle__option:active > span {
    transform: none;
  }
}
</style>
